import SectionHeading from "./SectionHeading";

const principles = [
  {
    icon: "ri-file-shield-2-line",
    title: "信源可溯",
    text: "每一条关键信息都尽量标注公开来源与核实状态，把「谁说的」讲清楚，让读者能自己回查。",
    tone: "text-primary-600 bg-primary-100",
  },
  {
    icon: "ri-node-tree",
    title: "把脉络讲清",
    text: "不做碎片化贴吧式搬运，而是用时间线与背景梳理还原事件从发生到落点的完整路径。",
    tone: "text-accent-700 bg-accent-100",
  },
  {
    icon: "ri-scales-3-line",
    title: "不站队只还原",
    text: "我们呈现事实与多方观点，把「已证实」与「待核实」分开标注，把判断权交还给每一位吃瓜人。",
    tone: "text-secondary-700 bg-secondary-100",
  },
];

const flow = [
  { step: "01", label: "全网收集" },
  { step: "02", label: "信源筛选" },
  { step: "03", label: "交叉核实" },
  { step: "04", label: "时间线整理" },
  { step: "05", label: "发布与追踪" },
];

const voices = [
  {
    quote: "以前刷瓜刷半天都是拼图，吃瓜中心的盘点几分钟就能把来龙去脉讲清楚，真的省时间。",
    name: "朝九晚五打工人",
    tag: "速递忠实读者",
  },
  {
    quote: "最打动我的是会标注「未证实」，在满屏带节奏的环境里，这种克制太难得了。",
    name: "新闻系在读学生",
    tag: "深度栏目读者",
  },
  {
    quote: "每周盘点当睡前读物，补课效率极高，编辑部审美也很在线，读起来很舒服。",
    name: "自媒体小编",
    tag: "同行也在看",
  },
];

export default function AboutSection() {
  return (
    <section id="about" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
      <div className="chigua-container mx-auto">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:items-center">
          <div className="lg:col-span-3">
            <SectionHeading
              eyebrow="关于中心"
              title="把瓜吃明白，是我们的全部工作"
              desc="吃瓜中心诞生于一个朴素的想法：热点越来越碎、越来越快，与其在无数信息流里拼拼凑凑，不如有一个地方，把每一颗瓜从头到尾讲清楚。我们是一群喜欢琢磨「事件怎么发生」的人，用编辑的方法论，为每天海量的吃瓜信息做减法。"
            />
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              这里没有立场鲜明的「带节奏」，只有尽量靠近事实的整理。突发爆料、持续发酵、反转复盘，我们用统一的情报整理流程，
              让复杂的热点也能被快速、清楚地掌握。欢迎你在看懂之后，也把吃瓜中心推荐给同样好奇的朋友。
            </p>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {principles.map((p) => (
                <div key={p.title} className="rounded-2xl border border-background-200 bg-background-50 p-5">
                  <span className={`grid h-10 w-10 place-items-center rounded-2xl text-lg ${p.tone}`}>
                    <i className={p.icon}></i>
                  </span>
                  <h4 className="mt-3 font-display text-base text-foreground-900">{p.title}</h4>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-foreground-500">{p.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* method panel */}
          <aside className="rounded-[28px] bg-primary-600 p-6 text-background-50 lg:col-span-2 md:p-8">
            <h3 className="font-display text-lg">情报是怎么做出来的</h3>
            <p className="mt-2 text-sm text-primary-100">一套可复用的整理流程，保证每一期盘点质量稳定。</p>
            <ol className="mt-6 space-y-1">
              {flow.map((f, i) => (
                <li key={f.step} className="flex items-center gap-4 rounded-2xl px-3 py-2.5 transition-colors hover:bg-primary-500/50">
                  <span className="font-display w-8 text-lg text-background-50/70">{f.step}</span>
                  <span className="flex-1 text-[15px] font-medium">{f.label}</span>
                  {i < flow.length - 1 && <i className="ri-arrow-drop-down-line text-xl text-primary-200"></i>}
                </li>
              ))}
            </ol>
            <p className="mt-5 rounded-2xl bg-background-50/15 p-3 text-xs leading-relaxed text-primary-50">
              从收集到发布，编辑部坚持「能查证再动手，查不到就标注」，宁慢一分，不传错一句。
            </p>
          </aside>
        </div>

        {/* 口碑 */}
        <div className="mt-16">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-2xl bg-accent-500 text-background-50"><i className="ri-chat-quote-line"></i></span>
            <h3 className="font-display text-lg text-foreground-900">读者怎么说</h3>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-3">
            {voices.map((v) => (
              <figure key={v.name} className="rounded-2xl border border-background-200 bg-background-100 p-6">
                <i className="ri-double-quotes-l text-2xl text-primary-300"></i>
                <blockquote className="mt-2 text-sm leading-relaxed text-foreground-700">{v.quote}</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-primary-500 text-sm font-bold text-background-50">
                    {v.name.slice(0, 1)}
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-foreground-800">{v.name}</span>
                    <span className="block text-xs text-foreground-400">{v.tag}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}